import React, { useState, useCallback, Dispatch, useEffect, useRef } from 'react';
import { Box, Typography, Tooltip, CardContent, Button } from '@mui/material';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import { useSnackbar, SnackbarKey } from 'notistack';
import FmtpTable from 'src/components/common/FmtpTable/FmtpTable';
import { editUserPermissions } from 'src/api/userManagement';
import {
    EntityAccess,
    UserPermission,
    EntityType,
    Permissions,
    PermissionContext,
    DynamicReportPermissionName,
    REPORTS_PERMISSION_PREFIX,
} from '@lanterds/types';
import { SelectedRows } from '../../ShippingPortal/types';
import { CellContext, ColumnDef, ExpandedState, Row } from '@tanstack/react-table';
import { mapUserPermissions, combineHardcodedAndDynamicPermissionDisplayNames } from 'src/util/helpers';
import { MappedPermission, FetchedUserPermissions, SubRow } from './types';
import { HardcodedPermissionDisplayNames } from 'src/util/constants';
import './PermissionsTable.scss';
import { useReportsContext } from '../../Reports/ReportsContext';

export function PermissionsTable({
    userId,
    userPermissions,
    setUserPermissions,
    changedPermissions,
    isLoading,
    isLoadingMessage,
    adminPermissions,
}: {
    userId: string;
    userPermissions: FetchedUserPermissions;
    setUserPermissions: Dispatch<React.SetStateAction<UserPermission | null>>;
    changedPermissions: Partial<UserPermission> | null;
    isLoading?: boolean;
    isLoadingMessage?: string;
    adminPermissions: UserPermission;
}) {
    const [selectedRows, setSelectedRows] = useState<SelectedRows>({});
    const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const removeSnackbarRef = useRef<SnackbarKey | null>(null);
    const { reports } = useReportsContext();

    const { mutateAsync: updateUserPermissions, isPending: isUpdateUserPermissionsPending } = editUserPermissions(userId);

    useEffect(() => {
        setExpandedRows({});
    }, [changedPermissions]);

    const onExpandedChange = useCallback(
        async (row: () => ExpandedState) => {
            const rowNumber = Object.keys(row())[0];
            const newExpandedList = { ...expandedRows };

            if (!expandedRows[rowNumber]) {
                newExpandedList[rowNumber] = true;
                setExpandedRows(newExpandedList);
            } else {
                newExpandedList[rowNumber] = false;
                setExpandedRows(newExpandedList);
            }
        },
        [userPermissions, expandedRows],
    );

    const getPendingStamp = (subRows: SubRow[], permission: Permissions | DynamicReportPermissionName) => {
        if (subRows && changedPermissions && permission in changedPermissions) {
            return (
                <Tooltip arrow title="Please click Save to finish updating this permission." placement="right">
                    <Typography variant="caption" className="pending-save">
                        PENDING SAVE
                    </Typography>
                </Tooltip>
            );
        }
        return null;
    };

    const combinedDisplayNames = combineHardcodedAndDynamicPermissionDisplayNames(reports);

    const columns: ColumnDef<MappedPermission>[] = [
        {
            accessorKey: 'permission',
            header: 'Permission',
            meta: {
                isExpandable: true,
                innerHtml: ({ row }: CellContext<MappedPermission, unknown>) => {
                    const { permission, subRows } = row.original;

                    const displayName = combinedDisplayNames[permission];

                    return (
                        <Typography>
                            {displayName} {getPendingStamp(subRows, permission)}
                        </Typography>
                    );
                },
            },
        },
        {
            accessorKey: 'context',
            header: 'Context',
            size: 200,
            enableSorting: false,
            enableColumnFilter: false,
            meta: {
                innerHtml: ({ row }) => {
                    if (row.depth === 0) {
                        const mappedPermissions = row.original as MappedPermission;
                        const contextValue = mappedPermissions.context;
                        const subRows = mappedPermissions.subRows;

                        if (contextValue === PermissionContext.GLOBAL) {
                            return <Box className="context-cell">Global</Box>;
                        }

                        const firstType = subRows[0]?.type;

                        if (firstType === EntityType.ACCESS_ALL_CUSTOMERS) {
                            return <Box className="context-cell">All Customers</Box>;
                        }

                        const { dealerAccessList } = userPermissions;

                        const labels = subRows
                            .map((subRow) => {
                                if (subRow.type === 'customer') {
                                    // customers: just show the ID
                                    return subRow.id;
                                }
                                // dealers: find the full info and show its displayName
                                const dealer = dealerAccessList?.find((dealer) => dealer.accountId === subRow.id);
                                return dealer?.name ?? subRow.displayName;
                            })
                            .filter(Boolean) as string[];

                        const preview = labels.slice(0, 3).join(', ');

                        return <Box className="context-cell">{labels.length > 3 ? `${preview}…` : preview}</Box>;
                    } else {
                        const sr = row.original;
                        return <Typography>{sr.displayName}</Typography>;
                    }
                },
            },
        },
        {
            id: 'remove',
            size: 20,
            enableColumnFilter: false,
            enableResizing: false,
            meta: {
                innerHtml: ({ row }: CellContext<MappedPermission, unknown>) => {
                    return (
                        <Box className="delete-icon-container">
                            <DeleteTwoToneIcon color="primary" onClick={() => handleRemovePermission(row)} />
                        </Box>
                    );
                },
            },
        },
    ];

    const handleRemovePermission = async (row: Row<MappedPermission>) => {
        const parentRowId = row.id.split('.')[0];
        const subRows = row.originalSubRows;

        let selectedRowIds = [row.id];

        if (subRows) {
            selectedRowIds = [...selectedRowIds, ...subRows.map((row, index) => `${parentRowId}.${index}`)];
        }

        if (!selectedRowIds.length || !userPermissions) {
            alert('Please select a Permission before removing.');
        } else {
            // Get permissions data in the same way the table is constructed
            const mappedUserPermissions = mapUserPermissions(userPermissions, reports);

            const filteredPermissions: MappedPermission[] = [...mappedUserPermissions];

            for (const rowId of selectedRowIds) {
                if (rowId.includes('.')) {
                    // Is a sub row
                    const subRowId = rowId.split('.')[1];

                    const permissionItem = { ...filteredPermissions[Number(parentRowId)] };
                    const currentSelectedSubRow = mappedUserPermissions[Number(parentRowId)].subRows[Number(subRowId)];

                    if (
                        currentSelectedSubRow.type === EntityType.CUSTOMER ||
                        currentSelectedSubRow.type === EntityType.DEALER
                    ) {
                        permissionItem.subRows = permissionItem.subRows.filter((subRowItem) => {
                            if (subRowItem.type === EntityType.CUSTOMER || subRowItem.type === EntityType.DEALER) {
                                return subRowItem.id !== currentSelectedSubRow.id;
                            }
                        });

                        filteredPermissions[Number(parentRowId)] = permissionItem;
                    } else {
                        permissionItem.subRows = permissionItem.subRows.filter((subRowItem) => {
                            return subRowItem.type !== currentSelectedSubRow.type;
                        });

                        filteredPermissions[Number(parentRowId)] = permissionItem;
                    }
                }
            }

            const newUserPermissions: UserPermission = {};

            for (const userPermission of filteredPermissions) {
                const { permission, subRows } = userPermission;

                // If we end up with no subRows, then don't include the permission
                if (subRows.length) {
                    const entities = subRows.map<EntityAccess>((row) => {
                        if (row.type === EntityType.ACCESS_ALL_CUSTOMERS) {
                            return { type: EntityType.ACCESS_ALL_CUSTOMERS };
                        } else {
                            return { type: row.type as EntityType, id: row.id ?? '' };
                        }
                    });

                    const remainingEntities = newUserPermissions[permission] ?? [];

                    if (permission in newUserPermissions && Array.isArray(remainingEntities)) {
                        newUserPermissions[permission] = [...remainingEntities, ...entities];
                    } else {
                        newUserPermissions[permission] = [...entities];
                    }
                }
            }
            removeSnackbarRef.current = enqueueSnackbar('Removing permission…', {
                variant: 'loading',
                preventDuplicate: true,
                disableWindowBlurListener: true,
                autoHideDuration: null,
                action: (key) => (
                    <Button size="small" onClick={() => closeSnackbar(key)}>
                        Dismiss
                    </Button>
                ),
            });

            try {
                await updateUserPermissions(newUserPermissions);
                if (removeSnackbarRef.current) closeSnackbar(removeSnackbarRef.current);
                enqueueSnackbar('Permission removed.', {
                    variant: 'success',
                    preventDuplicate: true,
                });
                setSelectedRows({});
                setUserPermissions(null);
                setExpandedRows({});
            } catch (err) {
                console.error(err);
                if (removeSnackbarRef.current) closeSnackbar(removeSnackbarRef.current);
                enqueueSnackbar('Failed to remove permission.', {
                    variant: 'error',
                    preventDuplicate: true,
                });
            } finally {
                removeSnackbarRef.current = null;
            }
        }
    };

    function onlyIdEntities(allowedAccess?: EntityAccess[]): Array<EntityAccess & { id: string }> {
        return Array.isArray(allowedAccess)
            ? allowedAccess.filter((entity): entity is EntityAccess & { id: string } => 'id' in entity)
            : [];
    }

    function getFilteredSubRows(subRows: SubRow[], allowedAccess: EntityAccess[] | undefined): SubRow[] {
        const targetHasGlobal = subRows.some((subRow) => subRow.type === EntityType.ACCESS_ALL_CUSTOMERS);
        const viewerGlobal =
            Array.isArray(allowedAccess) && allowedAccess.some((allowed) => allowed.type === EntityType.ACCESS_ALL_CUSTOMERS);

        // Case A: viewerGlobal => see exactly what target has
        if (viewerGlobal) {
            return subRows;
        }

        // Case B: target is global but viewer limited => show only viewer's contexts
        if (targetHasGlobal && Array.isArray(allowedAccess)) {
            const allowedWithId = onlyIdEntities(allowedAccess);
            return allowedWithId.map(
                (allowed) =>
                    ({
                        id: allowed.id,
                        type: allowed.type,
                    }) as SubRow,
            );
        }

        // Case C: neither global => show intersection or nothing
        if (!Array.isArray(allowedAccess)) {
            return [];
        }
        const allowedWithId = onlyIdEntities(allowedAccess);
        return subRows.filter((subRow) =>
            allowedWithId.some((allowed) => allowed.id === subRow.id && allowed.type === subRow.type),
        );
    }

    // Only include permissions the current admin actually has
    const adminPermKeys = Object.keys(adminPermissions);
    const visiblePermissions: MappedPermission[] = mapUserPermissions(userPermissions, reports)
        // 1) only perms that the viewer actually has
        .filter((mappedPermission: MappedPermission) => {
            if (mappedPermission.permission.startsWith(REPORTS_PERMISSION_PREFIX) && adminPermissions.reports_all) {
                return true; // Allow all report permissions if the user has the reports_all permission
            }
            return adminPermKeys.includes(mappedPermission.permission);
        })
        // 2) for each, run our two-case global vs. limited filter
        .map((mappedPermission: MappedPermission) => {
            let allowed = adminPermissions[mappedPermission.permission] as EntityAccess[] | undefined;
            if (mappedPermission.permission.startsWith(REPORTS_PERMISSION_PREFIX) && adminPermissions.reports_all) {
                allowed = adminPermissions.reports_all as EntityAccess[];
            }
            const subRows = getFilteredSubRows(mappedPermission.subRows, allowed);
            return { ...mappedPermission, subRows };
        })
        // 3) drop any perms with zero visible contexts
        .filter((mappedPermission: MappedPermission) => mappedPermission.subRows.length > 0);

    return (
        <CardContent id="permission-edit-table" className="auto-fill-height">
            <FmtpTable
                data={visiblePermissions}
                columns={columns}
                setSelectedRows={setSelectedRows}
                selectedRows={selectedRows}
                enableSorting={true}
                isResizable={true}
                enableFilters={true}
                enableExpanding={true}
                getSubRows={(row: any) => row.subRows}
                expanded={expandedRows}
                onExpandedChange={onExpandedChange}
                isFetching={isUpdateUserPermissionsPending || isLoading}
                loadingIndicatorMessage={isUpdateUserPermissionsPending ? 'Removing Permissions...' : isLoadingMessage}
            />
        </CardContent>
    );
}
